import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X,
  Sparkles,
  MessageCircle,
  Quote,
} from "lucide-react";
import useReveal from "../hooks/useReveal";
import VideoModal, { VideoThumb } from "../components/VideoModal";
import { vpv, brand } from "../mock";

export default function VPV() {
  useReveal();
  const { hero, trust, imagine, intro, testimonials, benefits, closing } = vpv;

  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "VPV, Virtual Plant Visit for Manufacturers | Double Your Exports";

    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", content);
      return { tag, prev };
    };

    const d = setMeta(
      "description",
      "VPV, Virtual Plant Visit, is an immersive virtual factory tour that builds instant trust with international buyers, converting 80% of exhibition leads and onboarding buyers in 3 months, not 8 to 9."
    );
    const k = setMeta(
      "keywords",
      "VPV, Virtual Plant Visit, virtual factory tours, VPV for manufacturers, virtual factory tour, export lead conversion, Double Your Exports, Nitin Gupta"
    );

    return () => {
      document.title = prevTitle;
      if (d.prev != null) d.tag.setAttribute("content", d.prev);
      if (k.prev != null) k.tag.setAttribute("content", k.prev);
    };
  }, []);

  return (
    <main className="pt-20">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden py-20 lg:py-28">
        <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-[var(--crimson)]/15 blur-[130px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-1.5 mb-7">
              <Sparkles size={13} className="text-[var(--gold)]" />
              <h1 className="eyebrow text-[0.62rem] m-0">VPV - Virtual Plant Visit</h1>
            </div>
            <div className="flex items-end gap-4">
              <span className="font-serif-display text-7xl sm:text-8xl font-extrabold text-gradient-gold leading-none">
                {hero.stat}
              </span>
              <span className="text-[var(--muted)] pb-3 text-sm uppercase tracking-widest">
                lead<br />conversion
              </span>
            </div>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl font-extrabold leading-[1.05]">
              {hero.headline}
            </h2>
            <p className="mt-5 text-lg text-[var(--ivory)]/85 max-w-lg">{hero.sub}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href={`https://wa.me/${brand.phoneRaw}?text=I%20want%20to%20know%20more%20about%20VPV`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold px-7 py-3.5 rounded-full inline-flex items-center gap-2 group"
              >
                {hero.cta}
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/#solutions" className="btn-ghost px-7 py-3.5 rounded-full inline-flex items-center gap-2">
                Back to Solutions
              </Link>
            </div>
          </div>

          <div className="reveal">
            <VideoThumb videoId={hero.videoId} title={hero.headline} />
            <p className="mt-3 text-center text-sm text-[var(--muted)]">
              Watch: 80% conversion in 3 months, not 8 to 9.
            </p>
          </div>
        </div>
      </section>

      {/* ===== TRUST PROBLEM ===== */}
      <section className="py-20 lg:py-28 bg-[var(--ink-2)] border-y border-[var(--line)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <p className="eyebrow">{trust.eyebrow}</p>
            <h2 className="mt-5 font-serif-display text-3xl sm:text-5xl font-bold leading-[1.08]">
              {trust.title}
            </h2>
            <p className="mt-6 text-xl text-[var(--ivory)]/90 font-medium">{trust.lead}</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-10 items-start">
            <div className="space-y-4 reveal">
              <p className="text-sm uppercase tracking-widest text-[var(--muted)] mb-2">To make things worse…</p>
              {trust.worse.map((w, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1 w-6 h-6 rounded-full bg-[var(--crimson)]/15 flex items-center justify-center shrink-0">
                    <X size={13} className="text-[var(--crimson-bright)]" />
                  </span>
                  <span className="text-[var(--ivory)]/80">{w}</span>
                </div>
              ))}
            </div>

            <div className="card-lux rounded-3xl p-8 reveal">
              <h3 className="font-serif-display text-2xl font-bold">{trust.promiseTitle}</h3>
              <ul className="mt-6 space-y-4">
                {trust.promise.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-[var(--gold)]/15 flex items-center justify-center shrink-0">
                      <Check size={14} className="text-[var(--gold)]" />
                    </span>
                    <span className="text-[var(--ivory)]/90 font-medium">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 rounded-3xl p-8 sm:p-10 border border-[var(--gold)]/25 bg-[var(--gold)]/[0.04] reveal">
            <p className="text-lg sm:text-xl leading-relaxed text-[var(--ivory)]/90">{imagine.body}</p>
          </div>
        </div>
      </section>

      {/* ===== INTRO + DEMO ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <p className="eyebrow">{intro.eyebrow}</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-6xl font-extrabold leading-[1.02] text-gradient-gold">
              {intro.title}
            </h2>
            <p className="mt-3 font-serif-display text-xl text-[var(--ivory)]/90">{intro.subtitle}</p>
            <p className="mt-6 text-[var(--muted)] leading-relaxed">{intro.desc}</p>
          </div>
          <div className="reveal">
            <VideoThumb videoId={intro.demoVideoId} title="VPV Demo" />
            <p className="mt-3 text-center text-sm text-[var(--muted)]">Watch the VPV demo</p>
          </div>
        </div>
      </section>

      {/* ===== SUCCESS STORIES ===== */}
      <section className="py-20 lg:py-28 bg-[var(--ink-2)] border-y border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal">
            <p className="eyebrow">Real Success Stories</p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-5xl font-bold">
              See how VPV <span className="text-gradient-gold">changed the game</span>
            </h2>
            <p className="mt-5 text-[var(--muted)]">Real virtual factory tours, real export wins. Straight from the manufacturers who lived it.</p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 gap-8">
            {testimonials.map((t) => (
              <div key={t.videoId} className="card-lux rounded-3xl overflow-hidden reveal">
                <VideoThumb videoId={t.videoId} title={t.quote} className="rounded-none border-0" />
                <div className="p-6">
                  <div className="flex items-start gap-2">
                    <Quote size={18} className="text-[var(--gold)] mt-1 shrink-0" />
                    <p className="font-serif-display text-lg font-semibold leading-snug">{t.quote}</p>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold">{t.name}</div>
                      <div className="text-sm text-[var(--muted)]">{t.role}</div>
                      <div className="text-xs text-[var(--muted)]">{t.place}</div>
                    </div>
                    <span className="text-[0.65rem] uppercase tracking-widest text-[var(--gold)] border border-[var(--gold)]/30 rounded-full px-3 py-1 whitespace-nowrap">
                      {t.tag}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <p className="eyebrow">One Time Investment</p>
            <h2 className="mt-5 font-serif-display text-3xl sm:text-5xl font-bold leading-[1.08]">
              What you get, <span className="text-gradient-gold">every single time</span> a buyer clicks
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="card-lux rounded-2xl px-6 py-5 flex items-center gap-4 reveal"
                style={{ transitionDelay: `${(i % 2) * 80}ms` }}
              >
                <span className="font-serif-display text-lg font-bold text-[var(--gold)] w-8 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[var(--ivory)]/90">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CLOSING CTA ===== */}
      <section className="py-20 lg:py-28 bg-[var(--ink-2)] border-t border-[var(--line)] relative overflow-hidden">
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[var(--crimson)]/10 blur-[130px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center reveal">
          <h2 className="font-serif-display text-3xl sm:text-5xl font-bold leading-[1.1]">
            {closing.title}
          </h2>
          <p className="mt-6 text-lg text-[var(--muted)]">{closing.desc}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href={`https://wa.me/${brand.phoneRaw}?text=I%20want%20to%20know%20more%20about%20VPV`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-7 py-3.5 rounded-full inline-flex items-center gap-2"
            >
              <MessageCircle size={17} /> WhatsApp: I want to know more
            </a>
            <a href={`tel:${brand.phoneRaw}`} className="btn-ghost px-7 py-3.5 rounded-full inline-flex items-center gap-2">
              Call {brand.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
